import { useCallback, useMemo } from 'react';

import { useApi } from '@/contexts/ApiContext';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useNotifications } from '@/contexts/NotificationContext';

/**
 * Custom hook for authentication operations
 */
export const useAuthActions = () => {
  const { login, logout, user, isAuthenticated, isLoading } = useAuth();

  const hasRole = useCallback(
    (role: string) => {
      if (!user) return false;
      // Prefer normalized roleName if present
      if (user.roleName) return user.roleName === role;
      // Handle role as string id/name or populated object
      if (typeof user.role === 'string') return user.role === role;
      return user.role?.name === role;
    },
    [user]
  );

  const hasPermission = useCallback(
    (resource: string, action: string) => {
      // Normalize resource aliases used across app/api
      const normalizeResource = (res?: string) => {
        if (!res) return res;
        const map: Record<string, string> = {
          // Common aliases between UI and API
          sales_order: 'sales_order',
          purchase_order: 'purchase_orders',
          requisitions: 'purchase_requisitions',
          audit: 'audit_logs',
          transactions: 'payments',
          report: 'reports',
          analytics: 'dashboard',
        };
        return map[res] || res;
      };

      const normalizedResource = normalizeResource(resource);

      // Check if user has "all" resource permission first
      if (
        user?.hasAllPermissions &&
        (user?.permissions?.['all']?.includes(action) ?? false)
      ) {
        return true;
      }

      // Try exact resource, then fallback to normalized alias
      const byExact = user?.permissions?.[resource]?.includes(action) ?? false;
      const byAlias = normalizedResource
        ? (user?.permissions?.[normalizedResource]?.includes(action) ?? false)
        : false;

      return byExact || byAlias;
    },
    [user]
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => {
      return roles.some(role => hasRole(role));
    },
    [hasRole]
  );

  const hasAnyPermission = useCallback(
    (permissions: Array<{ resource: string; action: string }>) => {
      return permissions.some(({ resource, action }) =>
        hasPermission(resource, action)
      );
    },
    [hasPermission]
  );

  const isAdmin = useMemo(
    () => hasRole('admin') || hasRole('superAdmin'),
    [hasRole]
  );
  const isSuperAdmin = useMemo(() => hasRole('superAdmin'), [hasRole]);

  return {
    login,
    logout,
    user,
    isAuthenticated,
    isLoading,
    hasRole,
    hasPermission,
    hasAnyRole,
    hasAnyPermission,
    isAdmin,
    isSuperAdmin,
  };
};

/**
 * Custom hook for API operations with common patterns
 */
export const useApiActions = () => {
  const api = useApi();

  const fetchWithLoading = useCallback(
    async (
      operation: () => Promise<any>,
      loadingId?: string
    ): Promise<any | null> => {
      // Minimal implementation to avoid unused warnings; extend when needed
      void loadingId;
      try {
        const result = await operation();
        return result ?? null;
      } catch (err) {
        return null;
      }
    },
    []
  );

  const fetchPaginated = useCallback(
    async (
      endpoint: string,
      page: number = 1,
      limit: number = 10,
      params?: Record<string, any>
    ) => {
      const response = await api.get(endpoint, {
        page,
        limit,
        ...params,
      });

      return response;
    },
    [api]
  );

  return {
    ...api,
    fetchWithLoading,
    fetchPaginated,
  };
};

/**
 * Custom hook for notification operations
 */
export const useNotificationActions = () => {
  const notifications = useNotifications();

  const showSuccess = useCallback(
    (message: string, title?: string) => {
      notifications.showSuccess(title ? `${title}: ${message}` : message);
    },
    [notifications]
  );

  const showError = useCallback(
    (message: string, title?: string) => {
      notifications.showError(title ? `${title}: ${message}` : message);
    },
    [notifications]
  );

  const showInfo = useCallback(
    (message: string, title?: string) => {
      notifications.showInfo(title ? `${title}: ${message}` : message);
    },
    [notifications]
  );

  const showWarning = useCallback(
    (message: string, title?: string) => {
      notifications.showWarning(title ? `${title}: ${message}` : message);
    },
    [notifications]
  );

  const notifySuccess = useCallback(
    (operation: string) => {
      showSuccess(`${operation} completed successfully`);
    },
    [showSuccess]
  );

  const notifyError = useCallback(
    (operation: string, error?: any) => {
      const message = error?.message || error || 'An error occurred';
      showError(`${operation} failed: ${message}`);
    },
    [showError]
  );

  return {
    ...notifications,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    notifySuccess,
    notifyError,
  };
};

/**
 * Custom hook for theme operations
 */
export const useThemeActions = () => {
  // const theme = useTheme();
  // const applyTheme = useCallback(
  //   (themeName: string) => {
  //     switch (themeName) {
  //       case 'light':
  //         theme.setMode('light');
  //         break;
  //       case 'dark':
  //         theme.setMode('dark');
  //         break;
  //       case 'system':
  //         theme.setMode('system');
  //         break;
  //       default:
  //         theme.setMode('system');
  //     }
  //   },
  //   [theme]
  // );
  // const increaseFontSize = useCallback(() => {
  //   const sizes = ['small', 'medium', 'large'] as const;
  //   const currentIndex = sizes.indexOf(theme.preferences.fontSize);
  //   const nextIndex = Math.min(currentIndex + 1, sizes.length - 1);
  //   theme.setFontSize(sizes[nextIndex]);
  // }, [theme]);
  // const decreaseFontSize = useCallback(() => {
  //   const sizes = ['small', 'medium', 'large'] as const;
  //   const currentIndex = sizes.indexOf(theme.preferences.fontSize);
  //   const nextIndex = Math.max(currentIndex - 1, 0);
  //   theme.setFontSize(sizes[nextIndex]);
  // }, [theme]);
  // return {
  //   ...theme,
  //   applyTheme,
  //   increaseFontSize,
  //   decreaseFontSize,
  // };
};

/**
 * Custom hook for global state operations
 */
export const useGlobalStateActions = () => {
  const globalState = useGlobalState();

  const showConfirmSheet = useCallback(
    (
      title: string,
      content: string,
      onConfirm: () => void,
      onCancel?: () => void
    ) => {
      globalState.showSheet({
        id: `confirm-${Date.now()}`,
        type: 'confirm',
        title,
        content,
        onConfirm,
        onCancel,
        confirmText: 'Confirm',
        cancelText: 'Cancel',
      });
    },
    [globalState]
  );

  const showDeleteSheet = useCallback(
    (itemName: string, onConfirm: () => void, onCancel?: () => void) => {
      globalState.showSheet({
        id: `delete-${Date.now()}`,
        type: 'delete',
        title: 'Confirm Delete',
        content: `Are you sure you want to delete ${itemName}? This action cannot be undone.`,
        onConfirm,
        onCancel,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      });
    },
    [globalState]
  );

  const withLoading = useCallback(
    async (
      operation: () => Promise<any>,
      loadingId: string,
      loadingMessage?: string
    ): Promise<any | null> => {
      globalState.showLoading(loadingId, loadingMessage);

      try {
        const result = await operation();
        return result;
      } catch (error) {
        return null;
      } finally {
        globalState.hideLoading(loadingId);
      }
    },
    [globalState]
  );

  return {
    ...globalState,
    showConfirmSheet,
    showDeleteSheet,
    withLoading,
  };
};

/**
 * Combined hook that provides all app context actions
 */
export const useAppActions = () => {
  const auth = useAuthActions();
  const api = useApiActions();
  const notifications = useNotificationActions();
  // const theme = useThemeActions();
  const globalState = useGlobalStateActions();

  return {
    auth,
    api,
    notifications,
    // theme,
    globalState,
  };
};

/**
 * Hook to get current user profile with common computed properties
 */
export const useUserProfile = () => {
  const { user } = useAuth();

  const profile = useMemo(() => {
    if (!user) return null;

    return {
      ...user,
      displayName: user.username || user.email,
      initials: user.username
        ? user.username.substring(0, 2).toUpperCase()
        : user.email.substring(0, 2).toUpperCase(),
      isActive: user.isActive ?? true,
      lastLoginDate: user.lastLogin ? new Date(user.lastLogin) : null,
      createdDate: user.createdAt ? new Date(user.createdAt) : null,
      updatedDate: user.updatedAt ? new Date(user.updatedAt) : null,
    };
  }, [user]);

  return profile;
};
